/**
 * Travel & Cities — VIP-only page for:
 *   • Travel Mode        (single location override; auto-expires in 14 days)
 *   • Multi-City Mode    (up to 3 additional cities appended to home)
 *   • Hometown           (display-only field shown at bottom of profile)
 *
 * Free/Premium users see the page but any save action prompts the VIP paywall.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, Plane, Home, Loader2, X, Search, Crown, Trash2 } from 'lucide-react';

const CityPicker = ({ label, placeholder, onPick, testIdPrefix }) => {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault?.();
    if (!q.trim() || q.trim().length < 2) return;
    setLoading(true);
    try {
      const r = await api.get(`/geo/search`, { params: { q: q.trim(), limit: 5 } });
      setRows(r.data.results || []);
    } catch {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`${testIdPrefix}-picker`}>
      <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold block mb-2">{label}</label>
      <form onSubmit={search} className="flex gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testIdPrefix}-input`}
          className="flex-1 px-3 py-2.5 rounded-lg border border-[var(--hairline-strong)] text-sm focus:outline-none focus:border-[#B91C1C]"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid={`${testIdPrefix}-search-btn`}
          className="px-3 py-2.5 rounded-lg bg-black/[0.04] hover:bg-black/[0.08] text-[#0F0F10]"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
        </button>
      </form>
      {rows.length > 0 && (
        <div className="mt-2 border border-[var(--hairline)] rounded-lg overflow-hidden max-h-60 overflow-y-auto" data-testid={`${testIdPrefix}-results`}>
          {rows.map((r, i) => (
            <button
              key={`${r.lat}-${r.lng}-${i}`}
              type="button"
              onClick={() => { onPick(r); setRows([]); setQ(''); }}
              data-testid={`${testIdPrefix}-result-${i}`}
              className="w-full text-left px-3 py-2 hover:bg-black/[0.03] text-[13px] border-b border-[var(--hairline)] last:border-b-0"
            >
              <div className="font-semibold text-[#0F0F10]">
                {r.city || r.display_name?.split(',')[0]}
              </div>
              <div className="text-[11px] text-[#8A8A93] truncate">{r.display_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function TravelMode() {
  const navigate = useNavigate();
  const [tier, setTier] = useState('free');
  const [tm, setTm] = useState(null);
  const [maxDays, setMaxDays] = useState(14);
  const [cities, setCities] = useState([]);
  const [maxCities, setMaxCities] = useState(3);
  const [hometown, setHometown] = useState({ city: '', state: '', country: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/subscriptions/my').then(r => setTier(r.data.subscription?.tier || 'free')).catch(() => {}),
      api.get('/profile/travel-mode').then(r => { setTm(r.data.travel_mode); setMaxDays(r.data.max_days); }).catch(() => {}),
      api.get('/profile/multi-city').then(r => { setCities(r.data.cities || []); setMaxCities(r.data.max_cities); }).catch(() => {}),
      api.get('/profile').then(r => {
        const p = r.data || {};
        if (p.hometown) setHometown({
          city: p.hometown.city || '',
          state: p.hometown.state || '',
          country: p.hometown.country || '',
        });
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const isVIP = tier === 'vip';

  const activateTravel = async (row) => {
    if (!isVIP) { toast.error('Travel Mode is a VIP feature'); navigate('/store?upgrade=vip'); return; }
    setSaving(true);
    try {
      const r = await api.post('/profile/travel-mode', {
        city: row.city || row.display_name?.split(',')[0] || 'Unknown',
        state: row.state,
        country: row.country,
        lat: row.lat,
        lng: row.lng,
      });
      setTm(r.data.travel_mode);
      toast.success(`Travel Mode on for ${r.data.travel_mode.city}`);
    } catch (e) {
      if (e.response?.status === 403) { toast.error(e.response.data.detail); navigate('/store?upgrade=vip'); }
      else toast.error('Failed to enable travel mode');
    } finally { setSaving(false); }
  };

  const clearTravel = async () => {
    try { await api.delete('/profile/travel-mode'); setTm(null); toast.success('Travel Mode off'); }
    catch { toast.error('Failed'); }
  };

  const addCity = async (row) => {
    if (!isVIP) { toast.error('Multi-City is a VIP feature'); navigate('/store?upgrade=vip'); return; }
    if (cities.length >= maxCities) { toast.error(`Max ${maxCities} cities`); return; }
    const next = [...cities, {
      city: row.city || row.display_name?.split(',')[0] || 'Unknown',
      state: row.state, country: row.country, lat: row.lat, lng: row.lng,
    }];
    setSaving(true);
    try {
      const r = await api.put('/profile/multi-city', { cities: next });
      setCities(r.data.cities);
      toast.success('City added');
    } catch (e) {
      if (e.response?.status === 403) { toast.error(e.response.data.detail); navigate('/store?upgrade=vip'); }
      else toast.error('Failed');
    } finally { setSaving(false); }
  };

  const removeCity = async (idx) => {
    const next = cities.filter((_, i) => i !== idx);
    setSaving(true);
    try {
      const r = await api.put('/profile/multi-city', { cities: next });
      setCities(r.data.cities);
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const saveHometown = async () => {
    setSaving(true);
    try {
      await api.put('/profile', { hometown });
      toast.success('Hometown saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const daysLeft = tm?.expires_at
    ? Math.max(0, Math.ceil((new Date(tm.expires_at) - new Date()) / (1000 * 60 * 60 * 24)))
    : 0;

  if (loading) return (
    <div className="min-h-screen bg-white flex items-center justify-center">
      <Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white pb-[80px]" data-testid="travel-mode-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} data-testid="travel-back" aria-label="Back"
          className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">LOCATION</p>
          <h1 className="font-brand text-[26px] font-[500] tracking-tight text-[#0F0F10] leading-tight">Travel & Cities</h1>
        </div>
      </div>

      {!isVIP && (
        <div className="mx-5 mb-4 rounded-2xl p-4 border border-[#B38138]/30 bg-[#FEF7ED]" data-testid="travel-vip-banner">
          <div className="flex items-start gap-3">
            <Crown size={18} className="text-[#B38138] mt-0.5" />
            <div className="flex-1">
              <p className="text-[13px] font-bold text-[#0F0F10]">VIP feature</p>
              <p className="text-[11.5px] text-[#4B4B52] mt-0.5">
                Travel Mode & Multi-City require VIP. Meet people wherever you&apos;re headed.
              </p>
              <button
                onClick={() => navigate('/store?upgrade=vip')}
                data-testid="travel-upgrade-vip"
                className="mt-2 px-4 py-1.5 rounded-full bg-[#B38138] text-white text-[11.5px] font-semibold"
              >
                Upgrade to VIP
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Travel Mode */}
      <section className="px-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Plane size={16} className="text-[#B91C1C]" />
          <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10]">Travel Mode</h2>
          <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#B38138] bg-[#FEF7ED] px-2 py-0.5 rounded-full">VIP</span>
        </div>
        <p className="text-[12px] text-[#8A8A93] mb-3">
          Browse & be seen in another city for up to {maxDays} days. Auto-turns off after that.
        </p>
        {tm ? (
          <div className="rounded-2xl border border-black/[0.06] bg-white p-4" data-testid="travel-active-card">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[15px] font-semibold text-[#0F0F10]">{tm.city}</span>
              <span className="inline-flex items-center gap-1 text-[10px] uppercase font-bold tracking-wider text-[#15803D] bg-[#F0FDF4] px-2 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#15803D]" />
                Active
              </span>
            </div>
            <p className="text-[11.5px] text-[#8A8A93]">
              {daysLeft} day{daysLeft === 1 ? '' : 's'} left · expires {new Date(tm.expires_at).toLocaleDateString()}
            </p>
            <button
              onClick={clearTravel}
              data-testid="travel-clear-btn"
              className="mt-3 px-4 py-2 rounded-full border border-[var(--hairline-strong)] text-[12px] font-semibold text-[#4B4B52]"
            >
              Turn off
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-black/[0.06] bg-white p-4">
            <CityPicker
              label="Set destination city"
              placeholder="e.g. Paris, France"
              testIdPrefix="travel"
              onPick={activateTravel}
            />
          </div>
        )}
      </section>

      {/* Multi-City */}
      <section className="px-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <MapPin size={16} className="text-[#B91C1C]" />
          <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10]">Multi-City</h2>
          <span className="text-[10px] font-bold uppercase tracking-[1.2px] text-[#B38138] bg-[#FEF7ED] px-2 py-0.5 rounded-full">VIP</span>
          <span className="ml-auto text-[11px] text-[#8A8A93] font-semibold tabular-nums">{cities.length}/{maxCities}</span>
        </div>
        <p className="text-[12px] text-[#8A8A93] mb-3">
          See profiles from up to {maxCities} extra cities in addition to your home city.
        </p>

        {cities.length > 0 && (
          <div className="rounded-2xl border border-black/[0.06] bg-white overflow-hidden mb-3" data-testid="multi-city-list">
            {cities.map((c, i) => (
              <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-[var(--hairline)] last:border-b-0" data-testid={`multi-city-row-${i}`}>
                <div className="flex-1">
                  <p className="text-[14px] font-semibold text-[#0F0F10]">{c.city}</p>
                  <p className="text-[11px] text-[#8A8A93]">
                    {[c.state, c.country].filter(Boolean).join(', ')}
                  </p>
                </div>
                <button
                  onClick={() => removeCity(i)}
                  disabled={saving}
                  data-testid={`multi-city-remove-${i}`}
                  className="w-8 h-8 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#B91C1C]"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        {cities.length < maxCities && (
          <div className="rounded-2xl border border-black/[0.06] bg-white p-4">
            <CityPicker
              label="Add a city"
              placeholder="e.g. New York, USA"
              testIdPrefix="multi-city"
              onPick={addCity}
            />
          </div>
        )}
      </section>

      {/* Hometown */}
      <section className="px-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Home size={16} className="text-[#B91C1C]" />
          <h2 className="font-brand text-[19px] font-[500] tracking-tight text-[#0F0F10]">Hometown</h2>
        </div>
        <p className="text-[12px] text-[#8A8A93] mb-3">
          Where you grew up. Shown on your profile — doesn&apos;t affect discovery.
        </p>
        <div className="rounded-2xl border border-black/[0.06] bg-white p-4 space-y-3">
          <div>
            <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold block mb-1.5">City</label>
            <input
              type="text"
              value={hometown.city}
              onChange={(e) => setHometown((h) => ({ ...h, city: e.target.value }))}
              placeholder="Chicago"
              data-testid="hometown-city"
              className="w-full px-3 py-2.5 rounded-lg border border-[var(--hairline-strong)] text-sm focus:outline-none focus:border-[#B91C1C]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold block mb-1.5">State</label>
              <input
                type="text"
                value={hometown.state}
                onChange={(e) => setHometown((h) => ({ ...h, state: e.target.value }))}
                placeholder="IL"
                data-testid="hometown-state"
                className="w-full px-3 py-2.5 rounded-lg border border-[var(--hairline-strong)] text-sm focus:outline-none focus:border-[#B91C1C]"
              />
            </div>
            <div>
              <label className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold block mb-1.5">Country</label>
              <input
                type="text"
                value={hometown.country}
                onChange={(e) => setHometown((h) => ({ ...h, country: e.target.value }))}
                placeholder="USA"
                data-testid="hometown-country"
                className="w-full px-3 py-2.5 rounded-lg border border-[var(--hairline-strong)] text-sm focus:outline-none focus:border-[#B91C1C]"
              />
            </div>
          </div>
          <button
            onClick={saveHometown}
            disabled={saving || !hometown.city.trim()}
            data-testid="hometown-save-btn"
            className="w-full py-2.5 rounded-full brand-gradient text-white text-sm font-semibold disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save hometown'}
          </button>
        </div>
      </section>
    </div>
  );
}
